import { atom, selector } from 'recoil';
import { ClientFlowAction } from '~/graphql/types.client';
import { flowActions } from './actions';
import getSelectableNeighborIds from '../components/Builder/utils/getSelectableNeighborIds';
import getRemovableFromCopyIds from '../components/Builder/utils/getRemovableFromCopyIds';

export type InteractionUpdateAction = {
  type: 'updateAction';
  action: ClientFlowAction;
};

export type InteractionDragging = {
  type: 'dragging';
  action?: ClientFlowAction;
};

export type InteractionUpdateName = {
  type: 'updateName';
};

export type CopyPhase = 'selecting' | 'pasting' | 'choosingPathToConnect';

export type InteractionCopyAction = {
  type: 'copyAction';

  phase: CopyPhase;

  /**
   * Whether the user can enter the "selecting" phase to (de)select multiple
   * actions to copy. For a single-action copy we start directly in "pasting"
   * and do not allow returning to "selecting" (e.g. via Escape).
   */
  allowSelecting: boolean;

  // Actions that are being copied
  copiedActions: Array<ClientFlowAction>;

  // Used to reset actions to the state before copying started
  actionsBeforeCopy: Array<ClientFlowAction>;

  /**
   * Stores the original paste location (edge) that the user clicked
   * while in the "pasting" phase. This is needed because in the
   * "choosingPathToConnect" phase the user clicks a different edge
   * (under a copied IfElse) to select which path to connect.
   */
  pastePoint?: {
    source: string;
    target: string;
  };

  /**
   * Used to store the hover preview of the copy action
   */
  copyHoverPreview: {
    from: null | { x: number; y: number; edgeKey: string };
    to: null | { x: number; y: number; edgeKey: string };
  };
};

export type InteractionConnectAction = {
  type: 'connectAction';
  action: ClientFlowAction;
  validTargets: Array<ClientFlowAction>;
  childIdKey?: 'trueChildId' | 'falseChildId';
};

export type InteractionHighlightActions = {
  type: 'highlightActions';
  highlightedActions: Array<string>;
};

export type Interaction =
  | InteractionDragging
  | InteractionUpdateAction
  | InteractionCopyAction
  | InteractionUpdateName
  | InteractionConnectAction
  | InteractionHighlightActions;

/** Disables some edges */
export const shouldDisable = selector({
  key: 'interactionShouldDisable',
  get: ({ get }) => {
    const currentInteraction = get(interactions);

    return (
      currentInteraction?.type === 'highlightActions' ||
      currentInteraction?.type === 'connectAction' ||
      currentInteraction?.type === 'copyAction'
    );
  },
});

/** During the selecting phase, IDs of actions that can be added to the selection */
export const copySelectableIds = selector<Set<string>>({
  key: 'interactionCopySelectableIds',
  get: ({ get }) => {
    const currentInteraction = get(interactions);
    if (
      currentInteraction?.type !== 'copyAction' ||
      currentInteraction.phase !== 'selecting'
    )
      return new Set();

    const allActions = get(flowActions);
    return getSelectableNeighborIds({
      selectedActions: currentInteraction.copiedActions,
      allActions,
    });
  },
});

/** During the selecting phase, IDs of actions that can be removed from the selection.
 * Only actions at the top or bottom of the branch can be removed. */
export const copyRemovableIds = selector<Set<string>>({
  key: 'interactionCopyRemovableIds',
  get: ({ get }) => {
    const currentInteraction = get(interactions);
    if (
      currentInteraction?.type !== 'copyAction' ||
      currentInteraction.phase !== 'selecting'
    )
      return new Set();

    return getRemovableFromCopyIds({
      selectedActions: currentInteraction.copiedActions,
    });
  },
});

const interactions = atom<Interaction | null>({
  key: 'flowbuilderActiveInteraction',
  default: null,
});

export default interactions;
